// HUD button bar — persistent action row visible only during phase=playing.
// Wires keyboard shortcuts + click handlers to all major panels (HOO-39..44, HOO-42).

import type { Net } from "../net.js";
import type { ClientStore } from "../state.js";
import { openCharSheet } from "./char_sheet.js";
import { openInventory } from "./inventory.js";
import { openQuestLog } from "./quest_log.js";
import { openSkillTree } from "./skill_tree.js";
import { openHelpPanel } from "./help.js";
import { getSettings, onSettings } from "./settings.js";

interface HudEntry {
  id: "character" | "inventory" | "quests" | "skills" | "help";
  key: string;
  icon: string;
  label: string;
  open: (host: HTMLElement, net: Net, store: ClientStore) => HTMLElement;
}

const ENTRIES: HudEntry[] = [
  { id: "character", key: "c", icon: "👤", label: "Character (C)", open: (h, n, s) => openCharSheet(h, n, s) },
  { id: "inventory", key: "i", icon: "🎒", label: "Inventory (I)", open: (h, n, s) => openInventory(h, n, s) },
  { id: "quests",    key: "q", icon: "📜", label: "Quests (Q)",    open: (h, n, s) => openQuestLog(h, n, s) },
  { id: "skills",    key: "k", icon: "🌳", label: "Skills (K)",    open: (h, n, s) => openSkillTree(h, n, s) },
  { id: "help",      key: "?", icon: "❓", label: "Help (?)",      open: (h)       => openHelpPanel(h) },
];

export function mountHudBar(uiHost: HTMLElement, net: Net, store: ClientStore): void {
  // Render container
  const bar = document.createElement("div");
  bar.className = "hud-bar";
  bar.id = "hud-bar";
  bar.style.display = "none";
  bar.innerHTML = ENTRIES.map((e) => `
    <button class="hud-btn" data-panel="${e.id}" title="${e.label}">
      <span class="hud-btn-icon">${e.icon}</span>
      <span class="hud-btn-key">${e.key.toUpperCase()}</span>
    </button>
  `).join("");
  document.body.appendChild(bar);

  // Click handlers — toggle panel via store.
  for (const btn of bar.querySelectorAll<HTMLButtonElement>(".hud-btn")) {
    const id = btn.dataset.panel as HudEntry["id"];
    btn.addEventListener("click", () => togglePanel(id));
  }

  // Keyboard shortcuts (disabled while typing in command input).
  document.addEventListener("keydown", (ev) => {
    const target = ev.target as HTMLElement | null;
    if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
    const entry = ENTRIES.find((e) => e.key === ev.key.toLowerCase()) ?? ENTRIES.find((e) => e.key === ev.key);
    if (!entry) return;
    ev.preventDefault();
    togglePanel(entry.id);
  });

  // Grammar gate: only visible during phase=playing + if the setting allows.
  function refresh(): void {
    const s = store.get();
    const cfg = getSettings();
    bar.style.display = s.phase === "playing" && cfg.show_hud_bar ? "" : "none";
    for (const btn of bar.querySelectorAll<HTMLButtonElement>(".hud-btn")) {
      btn.classList.toggle("active", btn.dataset.panel === s.open_panel);
    }
  }
  store.subscribe(refresh);
  onSettings(refresh);
  refresh();

  /** Close any open panel via its registered __cleanup() hook (which
   *  unsubscribes from store + removes the overlay + clears open_panel). */
  function closeOpenPanels(): void {
    const selectors = [".char-sheet-overlay", ".inv-overlay", ".quest-overlay", ".skill-overlay", ".help-overlay"];
    // Look at body too — overlays are appended to `host` (uiHost) for some
    // panels, but help.ts appends to document.body when no host is passed.
    for (const root of [uiHost, document.body] as const) {
      for (const sel of selectors) {
        for (const el of root.querySelectorAll<HTMLElement>(sel)) {
          const cleanup = (el as unknown as { __cleanup?: () => void }).__cleanup;
          if (typeof cleanup === "function") cleanup();
          else el.remove();
        }
      }
    }
  }

  function togglePanel(id: HudEntry["id"]): void {
    const currentState = store.get();
    if (currentState.open_panel === id) {
      closeOpenPanels();
      store.closePanel();
      return;
    }
    closeOpenPanels();
    const entry = ENTRIES.find((e) => e.id === id);
    if (!entry) return;
    entry.open(uiHost, net, store);
    store.openPanel(id);
  }
}
